import { useThemeColor } from '@tinycld/core/lib/use-app-theme'
import { Modal, ModalBackdrop, ModalContent } from '@tinycld/core/ui/modal'
import { AlertTriangle } from 'lucide-react-native'
import { useEffect, useState } from 'react'
import { Pressable, ScrollView, Text, TextInput, View } from 'react-native'
import type { DropReport, PendingChange } from './use-package-versions'
import { formatVersion } from './version-compare'

// ConfirmChangesModal gates Apply. For an upgrade-only set it is a simple
// confirm; when the set includes downgrades it loads a drop report for EVERY
// downgraded package and requires the operator to type each downgraded slug
// (comma/space separated) — so confirming one package can never silently
// downgrade another. An apply failure is surfaced inline (the modal stays open).
export function ConfirmChangesModal({
    isOpen,
    pendingChanges,
    hasDowngrade,
    fetchDropReport,
    onCancel,
    onConfirm,
}: {
    isOpen: boolean
    pendingChanges: PendingChange[]
    hasDowngrade: boolean
    fetchDropReport: (slug: string, targetVersion: string) => Promise<DropReport>
    onCancel: () => void
    onConfirm: () => Promise<void>
}) {
    const dangerColor = useThemeColor('danger')
    const fgColor = useThemeColor('foreground')
    const [typed, setTyped] = useState('')
    const [reports, setReports] = useState<Record<string, DropReport>>({})
    const [loadingReports, setLoadingReports] = useState(false)
    const [submitting, setSubmitting] = useState(false)
    const [submitError, setSubmitError] = useState<string | null>(null)

    const downgrades = pendingChanges.filter(c => c.isDowngrade)
    const downgradeSlugs = downgrades.map(c => c.slug)
    // Stable string key of the downgraded {slug→version} set — used as the effect
    // dep so it re-runs only when the set (or a target version) actually changes,
    // not on every render. Pairs are re-parsed from it inside the effect.
    const downgradeKey = downgrades.map(c => `${c.slug}@${c.targetVersion}`).join(',')

    // Load a drop report for EVERY downgraded package when the confirm opens.
    // Reset reports at the top so a previous open's data never flashes through.
    useEffect(() => {
        const pairs = downgradeKey ? downgradeKey.split(',').map(p => p.split('@')) : []
        if (!isOpen || pairs.length === 0) return
        let cancelled = false
        setReports({})
        setLoadingReports(true)
        Promise.all(
            pairs.map(([slug, version]) =>
                fetchDropReport(slug, version)
                    .then(r => [slug, r] as const)
                    .catch(() => [slug, { droppedCollections: [], droppedFields: [] }] as const)
            )
        )
            .then(entries => !cancelled && setReports(Object.fromEntries(entries)))
            .finally(() => !cancelled && setLoadingReports(false))
        return () => {
            cancelled = true
        }
    }, [isOpen, downgradeKey, fetchDropReport])

    if (!isOpen) return null

    const typedSlugs = new Set(
        typed
            .split(/[,\s]+/)
            .map(s => s.trim())
            .filter(Boolean)
    )
    const allConfirmed = downgradeSlugs.every(s => typedSlugs.has(s))
    const confirmEnabled = (hasDowngrade ? allConfirmed : true) && !submitting

    const handleConfirm = async () => {
        setSubmitting(true)
        setSubmitError(null)
        try {
            await onConfirm()
            setTyped('')
            setReports({})
        } catch (err) {
            // Surface the failure inline instead of leaving the user with a
            // silently-closed modal and no feedback.
            setSubmitError(err instanceof Error ? err.message : 'Failed to apply changes')
        } finally {
            setSubmitting(false)
        }
    }

    const handleCancel = () => {
        setTyped('')
        setReports({})
        setSubmitError(null)
        onCancel()
    }

    const confirmHint =
        downgradeSlugs.length === 1
            ? `Type ${downgradeSlugs[0]} to confirm`
            : `Type each package name to confirm: ${downgradeSlugs.join(', ')}`

    return (
        <Modal isOpen onClose={handleCancel}>
            <ModalBackdrop />
            <ModalContent className="w-[520px] max-h-[560px] p-4 gap-3">
                <View className="flex-row gap-2 items-center">
                    {hasDowngrade && <AlertTriangle size={18} color={dangerColor} />}
                    <Text className="text-foreground" style={{ fontSize: 19, fontWeight: '600' }}>
                        {hasDowngrade ? 'Confirm downgrade' : 'Apply version changes'}
                    </Text>
                </View>

                {/* Only the (potentially long) change list scrolls; the confirm
                    input + action buttons below stay pinned so a large
                    multi-package downgrade can never push them off-screen. */}
                <ScrollView className="max-h-[340px]">
                    <ChangeTable
                        pendingChanges={pendingChanges}
                        reports={reports}
                        loadingReports={loadingReports}
                    />
                </ScrollView>

                {hasDowngrade && (
                    <Text className="text-muted-foreground" style={{ fontSize: 11 }}>
                        The database is backed up before the change.
                    </Text>
                )}

                {hasDowngrade && (
                    <View className="gap-1">
                        <Text className="text-muted-foreground" style={{ fontSize: 12 }}>
                            {confirmHint}
                        </Text>
                        <TextInput
                            value={typed}
                            onChangeText={setTyped}
                            autoCapitalize="none"
                            placeholder={downgradeSlugs.join(', ')}
                            className="rounded-lg border border-border px-3 py-2"
                            style={{ color: fgColor, fontSize: 14 }}
                        />
                    </View>
                )}

                {submitError && (
                    <View className="rounded-lg p-2 bg-danger-soft">
                        <Text className="text-danger" style={{ fontSize: 12 }}>
                            {submitError}
                        </Text>
                    </View>
                )}

                <View className="flex-row gap-3 justify-end">
                    <Pressable onPress={handleCancel} className="px-3 py-2" disabled={submitting}>
                        <Text className="text-foreground" style={{ fontSize: 13 }}>
                            Cancel
                        </Text>
                    </Pressable>
                    <Pressable
                        onPress={handleConfirm}
                        disabled={!confirmEnabled}
                        className={`px-4 py-2 rounded-lg ${hasDowngrade ? 'bg-danger' : 'bg-primary'} ${confirmEnabled ? 'opacity-100' : 'opacity-50'}`}
                    >
                        <Text
                            className={
                                hasDowngrade ? 'text-danger-foreground' : 'text-primary-foreground'
                            }
                            style={{ fontSize: 13, fontWeight: '600' }}
                        >
                            {submitting ? 'Applying…' : hasDowngrade ? 'Downgrade' : 'Apply'}
                        </Text>
                    </Pressable>
                </View>
            </ModalContent>
        </Modal>
    )
}

// ChangeTable lists every staged change as one row: the package + version move
// on the left, and its data-loss summary on the right — so a multi-package set
// shows each package's impact inline (not just the first), and a downgrade's
// dropped schema is read alongside the package it belongs to. The caller wraps
// this list in a bounded ScrollView so a long set scrolls without pushing the
// confirm controls off-screen.
function ChangeTable({
    pendingChanges,
    reports,
    loadingReports,
}: {
    pendingChanges: PendingChange[]
    reports: Record<string, DropReport>
    loadingReports: boolean
}) {
    return (
        <View className="rounded-lg border border-border overflow-hidden">
            {pendingChanges.map((change, i) => (
                <ChangeRow
                    key={change.slug}
                    change={change}
                    report={reports[change.slug]}
                    loading={loadingReports && change.isDowngrade}
                    isFirst={i === 0}
                />
            ))}
        </View>
    )
}

function ChangeRow({
    change,
    report,
    loading,
    isFirst,
}: {
    change: PendingChange
    report?: DropReport
    loading: boolean
    isFirst: boolean
}) {
    return (
        <View className={`flex-row gap-3 px-3 py-2.5 ${isFirst ? '' : 'border-t border-border'}`}>
            <View className="flex-1 gap-0.5">
                <Text className="text-foreground" style={{ fontSize: 13, fontWeight: '500' }}>
                    {change.slug}
                </Text>
                <Text className="text-muted-foreground" style={{ fontSize: 12 }}>
                    → {formatVersion(change.targetVersion)}
                    {change.isDowngrade ? ' · downgrade' : ''}
                </Text>
            </View>
            <View className="flex-1 items-end">
                <DropSummary change={change} report={report} loading={loading} />
            </View>
        </View>
    )
}

// DropSummary is the right-hand cell: only downgrades can drop data, so upgrades
// show a plain "no data loss". A downgrade shows its loading state, then either
// "No data loss" or the exact collections + fields it will drop.
function DropSummary({
    change,
    report,
    loading,
}: {
    change: PendingChange
    report?: DropReport
    loading: boolean
}) {
    if (!change.isDowngrade) {
        return (
            <Text className="text-muted-foreground" style={{ fontSize: 12 }}>
                No data loss
            </Text>
        )
    }
    if (loading || !report) {
        return (
            <Text className="text-muted-foreground" style={{ fontSize: 12 }}>
                Checking…
            </Text>
        )
    }
    const dropped = [
        ...report.droppedCollections.map(c => `collection ${c}`),
        ...report.droppedFields.map(f => `${f.collection}.${f.field}`),
    ]
    if (dropped.length === 0) {
        return (
            <Text className="text-muted-foreground" style={{ fontSize: 12 }}>
                No data loss
            </Text>
        )
    }
    return (
        <View className="items-end gap-0.5">
            <Text className="text-danger" style={{ fontSize: 12, fontWeight: '600' }}>
                Drops {dropDescription(report)}
            </Text>
            {dropped.map(line => (
                <Text
                    key={line}
                    className="text-danger"
                    style={{ fontSize: 11, textAlign: 'right' }}
                >
                    {line}
                </Text>
            ))}
        </View>
    )
}

// dropDescription summarizes the counts (e.g. "2 collections, 1 field") for the
// row header above the itemized list.
function dropDescription(report: DropReport): string {
    const parts: string[] = []
    const cols = report.droppedCollections.length
    const fields = report.droppedFields.length
    if (cols > 0) parts.push(`${cols} collection${cols === 1 ? '' : 's'}`)
    if (fields > 0) parts.push(`${fields} field${fields === 1 ? '' : 's'}`)
    return parts.join(', ')
}
